import React, { useState, useEffect } from 'react';
import { apiService } from '../../config/api';
import artworkImageService from '../../services/artworkImageService';

const ImageUploader = () => {
  const [file, setFile] = useState(null);
  const [preview, setPreview] = useState('');
  const [collectionPrefix, setCollectionPrefix] = useState('');
  const [createArtwork, setCreateArtwork] = useState(true);
  const [title, setTitle] = useState('');
  const [description, setDescription] = useState('');
  const [price, setPrice] = useState(150);
  const [category, setCategory] = useState('Islamic Calligraphy');
  const [featured, setFeatured] = useState(false);
  const [loading, setLoading] = useState(false);
  const [message, setMessage] = useState('');
  const [error, setError] = useState('');
  const [existingImages, setExistingImages] = useState([]);

  // Fetch existing images on component mount
  useEffect(() => {
    fetchExistingImages();
  }, []);

  const fetchExistingImages = async () => {
    try {
      const response = await apiService.get('/images');
      setExistingImages(response.images || []);
    } catch (err) {
      console.error('Error fetching images:', err);
      setError('Failed to load existing images');
    }
  };

  const handleFileChange = (e) => {
    const selectedFile = e.target.files[0];
    setFile(selectedFile);
    
    // Create preview URL
    if (selectedFile) {
      const reader = new FileReader();
      reader.onloadend = () => {
        setPreview(reader.result);
      };
      reader.readAsDataURL(selectedFile);
    } else {
      setPreview('');
    }
  };

  const handleUpload = async (e) => {
    e.preventDefault();
    
    if (!file) {
      setError('Please select a file to upload');
      return;
    }

    if (!collectionPrefix) {
      setError('Please enter a collection prefix (e.g., "1-The Art of Patience")');
      return;
    }
    
    setLoading(true);
    setError('');
    setMessage('');
    
    try {
      const formData = new FormData();
      formData.append('image', file);
      formData.append('collectionPrefix', collectionPrefix);
      
      if (createArtwork) {
        formData.append('createArtwork', 'true');
        formData.append('title', title || 'Untitled Artwork');
        formData.append('description', description || '');
        formData.append('price', price || 150);
        formData.append('category', category || 'Islamic Calligraphy');
        formData.append('featured', featured ? 'true' : 'false');
      }
      
      // Upload image
      const response = await fetch('/api/images/upload', {
        method: 'POST',
        body: formData,
      });
      
      const result = await response.json();
      
      if (!response.ok) {
        throw new Error(result.error || 'Upload failed');
      }
      
      setMessage(result.message);
      setFile(null);
      setPreview('');
      
      // Reset form if successful
      if (createArtwork) {
        setTitle('');
        setDescription('');
        setPrice(150);
        setCategory('Islamic Calligraphy');
        setFeatured(false);
      }
      
      // Refresh image list
      fetchExistingImages();
    } catch (err) {
      console.error('Error uploading image:', err);
      setError(err.message || 'Failed to upload image');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="container mx-auto p-4">
      <h2 className="text-2xl font-bold mb-6">Image Management</h2>
      
      {/* Upload Form */}
      <div className="bg-white p-6 rounded-lg shadow-md mb-8">
        <h3 className="text-xl font-semibold mb-4">Upload New Image</h3>
        
        <form onSubmit={handleUpload}>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <div>
              <div className="mb-4">
                <label className="block text-sm font-medium mb-1">
                  Image File *
                </label>
                <input
                  type="file"
                  accept="image/*"
                  onChange={handleFileChange}
                  className="w-full p-2 border rounded"
                />
              </div>
              
              <div className="mb-4">
                <label className="block text-sm font-medium mb-1">
                  Collection Prefix *
                </label>
                <input
                  type="text"
                  value={collectionPrefix}
                  onChange={(e) => setCollectionPrefix(e.target.value)}
                  placeholder="e.g., 1-The Art of Patience"
                  className="w-full p-2 border rounded"
                  required
                />
                <p className="text-xs text-gray-500 mt-1">
                  This will be used to categorize the image and as a filename prefix
                </p>
              </div>
              
              <div className="mb-4">
                <label className="flex items-center">
                  <input
                    type="checkbox"
                    checked={createArtwork}
                    onChange={(e) => setCreateArtwork(e.target.checked)}
                    className="mr-2"
                  />
                  <span className="text-sm font-medium">Create artwork entry in database</span>
                </label>
              </div>
              
              {createArtwork && (
                <>
                  <div className="mb-4">
                    <label className="block text-sm font-medium mb-1">
                      Artwork Title
                    </label>
                    <input
                      type="text"
                      value={title}
                      onChange={(e) => setTitle(e.target.value)}
                      placeholder="Artwork Title"
                      className="w-full p-2 border rounded"
                    />
                  </div>
                  
                  <div className="mb-4">
                    <label className="block text-sm font-medium mb-1">
                      Description
                    </label>
                    <textarea
                      value={description}
                      onChange={(e) => setDescription(e.target.value)}
                      placeholder="Artwork description"
                      className="w-full p-2 border rounded"
                      rows="3"
                    ></textarea>
                  </div>
                  
                  <div className="grid grid-cols-2 gap-4">
                    <div className="mb-4">
                      <label className="block text-sm font-medium mb-1">
                        Price ($)
                      </label>
                      <input
                        type="number"
                        value={price}
                        onChange={(e) => setPrice(Number(e.target.value))}
                        min="0"
                        step="0.01"
                        className="w-full p-2 border rounded"
                      />
                    </div>
                    
                    <div className="mb-4">
                      <label className="block text-sm font-medium mb-1">
                        Category
                      </label>
                      <input
                        type="text"
                        value={category}
                        onChange={(e) => setCategory(e.target.value)}
                        className="w-full p-2 border rounded"
                      />
                    </div>
                  </div>
                  
                  <div className="mb-4">
                    <label className="flex items-center">
                      <input
                        type="checkbox"
                        checked={featured}
                        onChange={(e) => setFeatured(e.target.checked)}
                        className="mr-2"
                      />
                      <span className="text-sm font-medium">Featured Artwork</span>
                    </label>
                  </div>
                </>
              )}
            </div>
            
            <div>
              {preview && (
                <div className="border rounded p-4 text-center">
                  <p className="text-sm font-medium mb-2">Image Preview</p>
                  <img
                    src={preview}
                    alt="Preview"
                    className="max-h-64 mx-auto"
                  />
                </div>
              )}
            </div>
          </div>
          
          {error && (
            <div className="bg-red-100 border border-red-400 text-red-700 px-4 py-3 rounded mb-4">
              {error}
            </div>
          )}
          
          {message && (
            <div className="bg-green-100 border border-green-400 text-green-700 px-4 py-3 rounded mb-4">
              {message}
            </div>
          )}
          
          <button
            type="submit"
            disabled={loading}
            className={`px-4 py-2 rounded font-medium ${
              loading
                ? 'bg-gray-300 text-gray-500 cursor-not-allowed'
                : 'bg-blue-600 text-white hover:bg-blue-700'
            }`}
          >
            {loading ? 'Uploading...' : 'Upload Image'}
          </button>
        </form>
      </div>
      
      {/* Existing Images Gallery */}
      <div className="bg-white p-6 rounded-lg shadow-md">
        <h3 className="text-xl font-semibold mb-4">Existing Images</h3>
        
        {existingImages.length === 0 ? (
          <p className="text-gray-500">No images found</p>
        ) : (
          <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4">
            {existingImages.map((imageUrl, index) => (
              <div key={index} className="border rounded p-2">
                <img
                  src={imageUrl}
                  alt={`Artwork ${index}`}
                  className="w-full h-40 object-cover object-center mb-2"
                />
                <p className="text-xs truncate">{imageUrl.split('/').pop()}</p>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
};

export default ImageUploader;
